import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import authService from '../services/authService';
import './VinculosPendentes.css';

const formatarValor = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatarData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');

function VinculosPendentes() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendentes, setPendentes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [selecao, setSelecao] = useState({}); // { [pendId]: { fornecedorId, filtro, salvar } }
  const [processando, setProcessando] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const [pendRes, fornRes] = await Promise.all([
        api.get('/vinculos-pendentes'),
        api.get('/fornecedores?limit=1000')
      ]);
      const pend = pendRes.data?.data || [];
      const forn = fornRes.data.fornecedores || fornRes.data || [];
      setPendentes(pend);
      setFornecedores(Array.isArray(forn) ? forn : []);
      const sel = {};
      pend.forEach(p => { sel[p._id] = { fornecedorId: '', filtro: '', salvar: true }; });
      setSelecao(sel);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar vínculos pendentes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const u = authService.getCurrentUser();
    setUser(u);
    if (!['super_admin', 'admin', 'gerente'].includes(u?.role)) {
      toast.error('Acesso restrito');
      navigate('/dashboard');
      return;
    }
    carregar();
  }, [navigate, carregar]);

  const setCampo = (id, campo, valor) =>
    setSelecao(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [campo]: valor } }));

  const fornecedoresFiltrados = (filtro) => {
    const f = (filtro || '').toLowerCase().trim();
    const arr = [...fornecedores].sort((a, b) =>
      (a.razaoSocial || a.nomeFantasia || '').localeCompare(b.razaoSocial || b.nomeFantasia || ''));
    if (!f) return arr.slice(0, 50);
    return arr.filter(x =>
      (x.razaoSocial || '').toLowerCase().includes(f) ||
      (x.nomeFantasia || '').toLowerCase().includes(f) ||
      (x.cnpjCpf || '').includes(f)
    ).slice(0, 50);
  };

  const confirmar = async (pend) => {
    const est = selecao[pend._id] || {};
    if (!est.fornecedorId) { toast.warn('Selecione o fornecedor correto'); return; }
    const forn = fornecedores.find(f => f._id === est.fornecedorId);
    const ok = window.confirm(
      `Vincular a OS ${pend.codigo} ao fornecedor "${forn?.razaoSocial || forn?.nomeFantasia}"?\n` +
      (est.salvar ? `O nome do Frotas ("${pend.fornecedorNomeFrotas}") será salvo como nome alternativo — próximas OS casam sozinhas.` : '')
    );
    if (!ok) return;
    try {
      setProcessando(pend._id);
      const { data } = await api.post(`/vinculos-pendentes/${pend._id}/resolver`, {
        fornecedorId: est.fornecedorId,
        salvarComoNomeAlternativo: est.salvar !== false
      });
      toast.success(data.message || 'Vínculo resolvido');
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Erro ao resolver vínculo');
    } finally {
      setProcessando(null);
    }
  };

  const descartar = async (pend) => {
    if (!window.confirm(`Descartar a OS ${pend.codigo}? Ela NÃO será criada no portal.`)) return;
    try {
      setProcessando(pend._id);
      await api.delete(`/vinculos-pendentes/${pend._id}`);
      toast.success('Vínculo descartado');
      await carregar();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao descartar');
    } finally {
      setProcessando(null);
    }
  };

  return (
    <div className="app-container">
      <Header user={user} />
      <div className="main-wrapper">
        <Sidebar user={user} />
        <main className="main-content">
          <div className="page-header">
            <div>
              <h1>🔗 Vínculos Pendentes</h1>
              <p className="page-subtitle">
                OS recebidas do Frotas cujo <strong>fornecedor não casou com segurança</strong>.
                Confirme o fornecedor correto para criar a OS — sem risco de atribuir errado.
              </p>
            </div>
            <span className="badge-total">{pendentes.length} pendente(s)</span>
          </div>

          {loading ? (
            <div className="loading-state"><p>Carregando...</p></div>
          ) : pendentes.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
              </svg>
              <p>Nenhum vínculo pendente</p>
              <span>Quando uma OS do Frotas não casar com um fornecedor, ela aparece aqui.</span>
            </div>
          ) : (
            pendentes.map(pend => {
              const est = selecao[pend._id] || {};
              return (
                <div className="vinculo-card" key={pend._id}>
                  <div className="vinculo-info">
                    <div><span>OS</span><strong>{pend.codigo}</strong></div>
                    <div><span>Fornecedor no Frotas</span><strong className="frota-nome">{pend.fornecedorNomeFrotas}</strong></div>
                    <div><span>Cliente</span><strong>{pend.clienteId?.razaoSocial || pend.clienteNome || '-'}</strong></div>
                    <div><span>Veículo/Placa</span><strong>{pend.veiculo || pend.placa || '-'}</strong></div>
                    <div><span>Valor</span><strong>{formatarValor(pend.valorFinal)}</strong></div>
                    <div><span>Recebida em</span><strong>{formatarData(pend.createdAt)}</strong></div>
                  </div>

                  <div className="vinculo-acao">
                    <label className="vinculo-label">Vincular ao fornecedor correto:</label>
                    <input
                      type="text"
                      className="vinculo-filtro"
                      placeholder="Filtrar por nome ou CNPJ..."
                      value={est.filtro || ''}
                      onChange={(e) => setCampo(pend._id, 'filtro', e.target.value)}
                    />
                    <select
                      className="vinculo-select"
                      value={est.fornecedorId || ''}
                      onChange={(e) => setCampo(pend._id, 'fornecedorId', e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {fornecedoresFiltrados(est.filtro).map(f => (
                        <option key={f._id} value={f._id}>
                          {(f.razaoSocial || f.nomeFantasia)}{f.cnpjCpf ? ` — ${f.cnpjCpf}` : ''}
                        </option>
                      ))}
                    </select>
                    <label className="vinculo-check">
                      <input
                        type="checkbox"
                        checked={est.salvar !== false}
                        onChange={(e) => setCampo(pend._id, 'salvar', e.target.checked)}
                      />
                      Salvar "{pend.fornecedorNomeFrotas}" como nome alternativo (casa sozinho no futuro)
                    </label>
                    <div className="vinculo-botoes">
                      <button
                        className="btn-confirmar-vinculo"
                        disabled={processando === pend._id || !est.fornecedorId}
                        onClick={() => confirmar(pend)}
                      >
                        {processando === pend._id ? 'Processando...' : 'Confirmar e criar OS'}
                      </button>
                      <button
                        className="btn-descartar-vinculo"
                        disabled={processando === pend._id}
                        onClick={() => descartar(pend)}
                      >
                        Descartar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default VinculosPendentes;
